import { LOCATIONS } from "@/content/locations";
import { BN_LOCATIONS } from "@/content/bn/locations";

export type NavLeaf = { to: string; label: string; hash?: string };
export type NavItem =
  | { kind: "link"; to: string; label: string }
  | { kind: "menu"; label: string; items: NavLeaf[] };

/**
 * Site-wide primary navigation. Single source of truth for both the desktop
 * and mobile renderers.
 */
export const NAV: NavItem[] = [
  { kind: "link", to: "/", label: "Home" },
  {
    kind: "menu",
    label: "Services",
    items: [
      { to: "/services", label: "All Services" },
      { to: "/pricing", label: "Pricing & Packages" },
      { to: "/services", hash: "seo", label: "SEO Strategy" },
      { to: "/services", hash: "local-seo", label: "Local SEO" },
      { to: "/services", hash: "technical-seo-audit", label: "Technical SEO Audit" },
      { to: "/services/digital-marketing", label: "Digital Marketing" },
      { to: "/services/aeo", label: "AEO — AI Citations" },
      { to: "/services/geo", label: "GEO — AI Overviews" },
      { to: "/seo-for", label: "SEO by Industry" },
      { to: "/seo-training", label: "SEO Training" },
      { to: "/free-seo-audit", label: "Free SEO Audit" },
      { to: "/ai-action-plan", label: "AI Action Plan" },
    ],
  },
  {
    kind: "menu",
    label: "Locations",
    items: [
      { to: "/seo-locations", label: "All Locations" },
      { to: "/seo-services", label: "City × Service Index" },
      ...LOCATIONS.map((l) => ({
        to: "/seo-services/$location",
        label: `SEO in ${l.city}`,
        hash: l.slug,
      })),
    ],
  },
  {
    kind: "menu",
    label: "Work",
    items: [
      { to: "/case-studies", label: "Case Studies" },
      { to: "/testimonials", label: "Testimonials" },
      { to: "/process", label: "Process" },
    ],
  },
  {
    kind: "menu",
    label: "Resources",
    items: [
      { to: "/blog", label: "Blog" },
      { to: "/resources", label: "Free Resources" },
      { to: "/seo-tools", label: "SEO Toolkit" },
      { to: "/free-seo-audit", label: "Free SEO Audit" },
      { to: "/ai-action-plan", label: "AI Action Plan" },
      { to: "/seo-expert-bangladesh", label: "SEO Expert in Bangladesh" },
      { to: "/best-digital-marketing-expert-bangladesh", label: "Best Digital Marketer (BD)" },
      { to: "/what-is-digital-marketing", label: "What Is Digital Marketing?" },
      { to: "/online-marketing-small-businesses", label: "Online Marketing for Small Businesses" },
      { to: "/how-to-improve-domain-authority", label: "How To Improve Domain Authority" },
      { to: "/how-to-check-domain-authority", label: "How To Check Domain Authority" },
      { to: "/moz-da-vs-ahrefs-dr", label: "Moz DA vs Ahrefs DR" },
    ],
  },
  {
    kind: "menu",
    label: "Tools",
    items: [
      { to: "/tools", label: "All Tools" },
      { to: "/tools/serp-simulator", label: "SERP Simulator" },
      { to: "/tools/schema-generator", label: "Schema Generator" },
      { to: "/tools/robots-tester", label: "Robots.txt Tester" },
      { to: "/free-seo-audit", label: "Free SEO Audit" },
      { to: "/ai-action-plan", label: "AI Action Plan" },
    ],
  },
  { kind: "link", to: "/about", label: "About" },
  { kind: "link", to: "/contact", label: "Contact" },
];

/**
 * Bangla primary navigation. Only links to `/bn/*` pages that actually exist;
 * sections without a Bangla counterpart point at the nearest Bangla hub,
 * matching `SECTION_TO_BN` in `src/lib/language-switch.ts`.
 */
export const BN_NAV: NavItem[] = [
  { kind: "link", to: "/bn", label: "হোম" },
  {
    kind: "menu",
    label: "সার্ভিস",
    items: [
      { to: "/bn/services", label: "সব সার্ভিস" },
      { to: "/bn/services/seo", label: "এসইও সার্ভিস" },
      { to: "/bn/services/aeo", label: "AEO — এআই সাইটেশন" },
      { to: "/bn/services/geo", label: "GEO — এআই ওভারভিউ" },
      { to: "/bn/pricing", label: "প্যাকেজ ও দাম" },
      { to: "/bn/seo-services", label: "শহর অনুযায়ী এসইও" },
    ],
  },
  {
    kind: "menu",
    label: "লোকেশন",
    items: [
      { to: "/bn/seo-services", label: "সব লোকেশন" },
      ...BN_LOCATIONS.map((l) => ({
        to: "/bn/seo-services/$location",
        label: `${l.cityBn}-এ এসইও`,
        hash: l.slug,
      })),
    ],
  },
  {
    kind: "menu",
    label: "কাজ",
    items: [{ to: "/bn/case-studies", label: "কেস স্টাডি" }],
  },
  {
    kind: "menu",
    label: "রিসোর্স",
    items: [
      { to: "/bn/blog", label: "ব্লগ" },
      { to: "/bn/free-seo-audit", label: "ফ্রি এসইও অডিট" },
      { to: "/bn/ai-action-plan", label: "এআই অ্যাকশন প্ল্যান" },
      { to: "/bn/online-marketing-small-businesses", label: "ছোট ব্যবসার অনলাইন মার্কেটিং" },
    ],
  },
  { kind: "link", to: "/bn/about", label: "আমার সম্পর্কে" },
  { kind: "link", to: "/bn/contact", label: "যোগাযোগ" },
];

/**
 * Shared `<Link>` prop builder for nav leaves. Centralizes the two route
 * shapes the menu currently mixes:
 *   - `/services` items use the `hash` field as a section anchor.
 *   - `/seo-services/$location` items use the `hash` field as the dynamic
 *     `location` param value.
 */
export function navLeafLinkProps(it: NavLeaf) {
  return {
    to: it.to,
    hash: it.to === "/services" ? it.hash : undefined,
    params:
      it.to === "/seo-services/$location" || it.to === "/bn/seo-services/$location"
        ? ({ location: it.hash! } as const)
        : (undefined as never),
  };
}